import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The "Active: [chip] [chip] Clear all" strip pattern used above filter
 * bars. Each chip is a clearable label ("Team: Discord ×"), the whole
 * row includes a trailing "Clear all" affordance.
 *
 * The visual is a labeled outline chip — `rounded-md` (per DESIGN.md's
 * "Pills and tags use rounded-md, not rounded-full"), muted border,
 * gold-tinted hover state. Never `rounded-full`; that pattern was the
 * pre-refactor drift.
 */

export interface ActiveFilter {
  /** Unique key for React reconciliation. */
  key: string;
  /** Short label including the field name where possible ("Team: Discord"). */
  label: string;
  /** Clears just this filter. */
  clear: () => void;
}

interface ActiveFiltersProps {
  filters: ActiveFilter[];
  /** Clears every active filter at once. Rendered as a trailing text link. */
  onClearAll?: () => void;
  className?: string;
}

export function ActiveFilters({
  filters,
  onClearAll,
  className,
}: ActiveFiltersProps): JSX.Element | null {
  if (filters.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 text-xs', className)}>
      <span className="text-muted-foreground">Active:</span>
      {filters.map((f) => (
        <ActiveFilterChip key={f.key} label={f.label} onClear={f.clear} />
      ))}
      {onClearAll ? (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-1 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
}

interface ActiveFilterChipProps {
  label: string;
  onClear: () => void;
}

export function ActiveFilterChip({ label, onClear }: ActiveFilterChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClear}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-input bg-card px-2 py-0.5 font-medium leading-none',
        'transition-colors duration-150 hover:border-brand/60 hover:text-brand',
      )}
      aria-label={`Clear ${label}`}
    >
      {label}
      <X className="h-3 w-3" strokeWidth={2} />
    </button>
  );
}
